import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchUserLoads = createAsyncThunk(
  'load/fetchUserLoads',
  async ({ page = 1, perPage = 50, sortBy = 'created_at', sortOrder = 'desc', status = 'all', search = '' }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/api/loads/user', {
        params: { 
          page, 
          per_page: perPage,
          sort_by: sortBy, 
          sort_order: sortOrder, 
          status,
          search,
          include: ['documents']
        }
      });

      return {
        loads: response.data.loads.map(load => ({
          ...load,
          documents: load.documents || []
        })),
        pagination: {
          total: response.data.total,
          pages: response.data.pages,
          page: response.data.page,
          perPage: response.data.per_page,
          nextPage: response.data.next_page
        }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch loads');
    }
  }
);

export const editLoad = createAsyncThunk(
  'load/editLoad',
  async ({ loadId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/api/loads/${loadId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update load');
    }
  }
);

export const cancelLoad = createAsyncThunk(
  'load/cancelLoad',
  async (loadId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/loads/${loadId}/cancel`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to cancel load');
    }
  }
);

export const fetchLoadDocuments = createAsyncThunk(
  'load/fetchLoadDocuments',
  async (loadId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/loads/${loadId}/documents`);
      return {
        loadId,
        documents: response.data.documents
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch documents');
    }
  }
);

export const fetchSingleLoad = createAsyncThunk(
  'load/fetchSingleLoad',
  async (loadId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/loads/${loadId}`);
      
      // No need for separate documents fetch as they're included in the response
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({ type: 'unauthorized', message: 'Authentication required' });
      }
      if (error.response?.status === 403) {
        return rejectWithValue({ type: 'forbidden', message: 'Access denied' });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch load');
    }
  }
);

const loadSlice = createSlice({
  name: 'load',
  initialState: {
    loads: [],
    status: 'idle',
    error: null,
    pagination: {
      total: 0,
      pages: 0,
      page: 1,
      perPage: 50,
      nextPage: null
    },
    lastSyncTimestamp: null,
    activeTab: 'active',
    cancelStatus: 'idle',
    cancelError: null,
  },
  reducers: {
    newLoadReceived: (state, action) => {
      state.loads.unshift(action.payload);
      state.pagination.total += 1;
    },
    updateLoad: (state, action) => {
      const index = state.loads.findIndex(load => load.id === action.payload.id);
      if (index !== -1) {
        state.loads[index] = {
          ...state.loads[index],
          ...action.payload,
          stops: action.payload.stops || state.loads[index].stops || [],
          bids: action.payload.bids || state.loads[index].bids || [],
          status_history: action.payload.status_history || state.loads[index].status_history,
          documents: action.payload.documents || state.loads[index].documents || []
        };
      }
    },
    deleteLoad: (state, action) => {
      state.loads = state.loads.filter(load => load.id !== action.payload);
      state.pagination.total -= 1;
    },
    setLastSyncTimestamp: (state, action) => {
      state.lastSyncTimestamp = action.payload;
    },
    addBidToLoad: (state, action) => {
      const { loadId, bid } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        if (!state.loads[loadIndex].bids) {
          state.loads[loadIndex].bids = [];
        }
        
        // Ensure bid has both naming conventions for compatibility
        const formattedBid = {
          ...bid,
          bid_type: bid.bid_type || bid.bidType || 'yes',
          bidType: bid.bid_type || bid.bidType || 'yes',
          alt_pickup_date: bid.alt_pickup_date || bid.altPickupDate,
          altPickupDate: bid.alt_pickup_date || bid.altPickupDate,
          alt_pickup_time: bid.alt_pickup_time || bid.altPickupTime,
          altPickupTime: bid.alt_pickup_time || bid.altPickupTime,
          alt_delivery_date: bid.alt_delivery_date || bid.altDeliveryDate,
          altDeliveryDate: bid.alt_delivery_date || bid.altDeliveryDate,
          alt_delivery_time: bid.alt_delivery_time || bid.altDeliveryTime,
          altDeliveryTime: bid.alt_delivery_time || bid.altDeliveryTime
        };
        
        const existingBidIndex = state.loads[loadIndex].bids.findIndex(b => b.id === formattedBid.id);
        if (existingBidIndex === -1) {
          state.loads[loadIndex].bids.push(formattedBid);
        } else {
          state.loads[loadIndex].bids[existingBidIndex] = formattedBid;
        }
      }
    },
    updateBidStatus: (state, action) => {
      const { loadId, bidId, status } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        const load = state.loads[loadIndex];
        if (load && load.bids) {
          const bidIndex = load.bids.findIndex(b => b.id === bidId);
          if (bidIndex !== -1) {
            load.bids[bidIndex].status = status;
            if (status === 'Accepted') {
              load.status = 'assigned';
              load.bids.forEach((bid, index) => {
                if (index !== bidIndex) {
                  bid.status = 'Rejected';
                }
              });
            }
          }
        }
      }
    },
    updateLoadStatus: (state, action) => {
      const { loadId, status } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        state.loads[loadIndex].status = status;
        
        // Normalize status to lowercase for consistent comparison
        const normalizedStatus = (status || '').toLowerCase();
        if ((state.activeTab === 'active' && normalizedStatus === 'cancelled') ||
            (state.activeTab === 'active' && normalizedStatus === 'completed')) {
          state.pagination.total -= 1;
        }
      }
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    resetCancelStatus: (state) => {
      state.cancelStatus = 'idle';
      state.cancelError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserLoads.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserLoads.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loads = action.payload.loads || [];
        // Map pagination from the thunk payload correctly
        const p = action.payload.pagination || {};
        state.pagination = {
          total: p.total || 0,
          pages: p.pages || 0,
          page: p.page || 1,
          perPage: p.perPage || 50,
          nextPage: p.nextPage || null
        };
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchUserLoads.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'An error occurred';
      })
      .addCase(editLoad.fulfilled, (state, action) => {
        const index = state.loads.findIndex(load => load.id === action.payload.load.id);
        if (index !== -1) {
          state.loads[index] = action.payload.load;
        }
      })
      .addCase(cancelLoad.pending, (state) => {
        state.cancelStatus = 'loading';
      })
      .addCase(cancelLoad.fulfilled, (state, action) => {
        state.cancelStatus = 'succeeded';
        const loadIndex = state.loads.findIndex(load => load.id === action.payload.load.id);
        if (loadIndex !== -1) {
          state.loads[loadIndex] = action.payload.load;
        }
      })
      .addCase(cancelLoad.rejected, (state, action) => {
        state.cancelStatus = 'failed';
        state.cancelError = action.payload;
      })
      .addCase(fetchLoadDocuments.fulfilled, (state, action) => {
        const { loadId, documents } = action.payload;
        const loadIndex = state.loads.findIndex(load => load.id === loadId);
        if (loadIndex !== -1) {
          state.loads[loadIndex].documents = documents.load_documents || [];
        }
      })
      .addCase(fetchSingleLoad.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSingleLoad.fulfilled, (state, action) => {
        const loadIndex = state.loads.findIndex(load => load.id === action.payload.id);
        if (loadIndex !== -1) {
          // Merge new data while preserving existing data not returned by API
          state.loads[loadIndex] = {
            ...state.loads[loadIndex],
            ...action.payload,
            // Ensure these arrays are always defined
            stops: action.payload.stops || [],
            documents: action.payload.documents || [],
            status_history: action.payload.status_history || [],
            bids: action.payload.bids || []
          };
        } else {
          state.loads.push({
            ...action.payload,
            stops: action.payload.stops || [],
            documents: action.payload.documents || [],
            status_history: action.payload.status_history || [],
            bids: action.payload.bids || []
          });
        }
        state.status = 'succeeded';
      })
      .addCase(fetchSingleLoad.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { 
  newLoadReceived, 
  updateLoad, 
  deleteLoad, 
  setLastSyncTimestamp, 
  addBidToLoad, 
  updateBidStatus,
  updateLoadStatus,
  setActiveTab,
  resetCancelStatus,
} = loadSlice.actions;
export default loadSlice.reducer;

// Add these selectors
export const selectLoads = (state) => state.load.loads;
export const selectLoadById = (state, loadId) => {
  const loads = state.load.loads;
  if (!loads) return null;
  // Support both integer ID and UUID lookups for backwards compatibility
  const idStr = loadId?.toString();
  return loads.find(load => 
    load.id?.toString() === idStr || 
    load.uuid === idStr ||
    load.uuid?.toLowerCase() === idStr?.toLowerCase()
  );
};
export const selectBidById = (state, loadId, bidId) => {
  const load = selectLoadById(state, loadId);
  return load && load.bids ? load.bids.find(bid => bid.id === bidId) : undefined;
};

export const selectActiveTab = (state) => state.load.activeTab;
